import { useEffect, useMemo, useRef, useState } from 'react';
import { ymGoal } from '@/lib/analytics';
import { API_URL } from '@/lib/backendUrl';
import MoonSignShiftNote from './MoonSignShiftNote';

/**
 * Полоса из трёх блоков расшифровки ПОД герой-секцией, видимая гостю сразу,
 * без единого клика.
 *
 * Зачем. Разделы расшифровки прятались за кнопками в правой колонке, и
 * человек уходил, не увидев ни строчки про себя. Здесь он читает три
 * настоящих текста про СВОЮ карту.
 *
 * Почему полоса, а не колонка. Внутри левой колонки блок удлинял герой
 * на ~600 px и уводил следующий экран из поля зрения. Теперь это низкая
 * горизонтальная полоса во всю ширину под герой-секцией: три столбца на
 * десктопе, вертикально на узком экране.
 *
 * Почему без обрыва и градиента. Тексты корпуса — однострочники в 60-130
 * символов. Обрыв на такой длине читается как поломка вёрстки, а не как
 * интрига. Крючок — число: сколько всего блоков собралось по его карте.
 *
 * Текст и число приходят с сервера (POST /api/natal/preview). Этот компонент
 * НАМЕРЕННО ничего не импортирует из @/data — иначе на страницу гостя
 * приехал бы весь корпус расшифровок (~660 КБ), которого там сейчас нет.
 *
 * Кнопки в правой колонке и панель под ними не трогаются: это другой вход
 * в ту же расшифровку.
 *
 * Кнопка «Открыть весь разбор» гостю разбор больше НЕ раскрывает: за 15.09
 * по рекламе её нажали 35 человек из 120 визитов, читали по 5-20 минут и не
 * регистрировались. Теперь она открывает то же окно регистрации, что и
 * «Скачать PDF карты» (NatalLimitModal), — вход различается параметром
 * place: pdf и full_report. Вошедшему onFullReport не передают, и у него
 * кнопка работает как раньше — раскрывает разделы.
 */

// Флаг «событие уже отправлено» — на визит, не на страницу: перезагрузка
// или второй расчёт в той же вкладке не должны считаться вторым показом.
const PREVIEW_SENT_KEY = 'natal_preview_sent';

const SECTION_CTA = {
  planets: 'Все планеты в знаках',
  houses: 'Все 12 домов',
  aspects: 'Все аспекты карты',
};

// Падеж слова «блок» при числе. Экспортируется: заголовок окна регистрации
// (NatalLimitModal, вход full_report) называет ТО ЖЕ число теми же словами,
// и расходиться эти два текста не должны.
export const pluralBlocks = (n) => {
  const tail100 = n % 100;
  const tail10 = n % 10;
  if (tail100 >= 11 && tail100 <= 14) return 'блоков';
  if (tail10 === 1) return 'блок';
  if (tail10 >= 2 && tail10 <= 4) return 'блока';
  return 'блоков';
};

/**
 * Подпись кнопки разбора. Живёт здесь одна на ОБА входа: кнопку в полосе
 * превью и закреплённую кнопку по низу экрана на телефоне
 * (NatalGuestFunnel). Текст владельца — правится только здесь, и сразу в
 * обоих местах, иначе два призыва разъедутся словами и числом.
 */
export function fullReportLabel(total) {
  return `Открыть весь разбор — ${total} ${pluralBlocks(total)} по твоей карте`;
}

/**
 * Цель Метрики на нажатие кнопки разбора.
 *
 * Цель ОДНА на оба входа (новых не заводим), а place говорит, откуда нажали:
 *   full_report   — кнопка в полосе превью;
 *   sticky_mobile — закреплённая кнопка по низу экрана на телефоне.
 * Те же имена мест, что у natal_limit в NatalLimitModal, — чтобы в Метрике
 * нажатие и показ окна сходились по одному ключу.
 */
export function trackFullReportCta(place) {
  ymGoal('natal_preview_cta', { place });
}

/**
 * Кнопка «Открыть весь разбор — N блоков по твоей карте».
 *
 * Отдельным компонентом, потому что на телефоне она стоит в двух местах:
 * в полосе превью (как была) и её дублем сразу под колесом — в стековой
 * раскладке полоса уезжала на полтора экрана ниже колеса, и двое из пяти
 * по Вебвизору её вообще не находили. Текст, число, падеж и событие
 * natal_preview_cta живут здесь, чтобы два входа не разъехались.
 *
 * onFullReport — гостевая ветка: окно регистрации вместо раскрытия блоков.
 * Без неё (вошедший) кнопка раскрывает разделы, как раньше.
 */
export function FullReportButton({ total, onOpenBlocks, onFullReport, className = '', testId = 'natal-preview-cta', place = 'full_report' }) {
  if (!total) return null;
  return (
    <button
      type="button"
      onClick={() => {
        trackFullReportCta(place);
        if (typeof onFullReport === 'function') {
          onFullReport(total);
          return;
        }
        if (typeof onOpenBlocks === 'function') onOpenBlocks();
      }}
      className={`rounded-lg bg-sky-600 px-5 py-2 text-[13px] font-semibold
                       text-white transition-colors hover:bg-sky-700 ${className}`}
      data-testid={testId}
    >
      {fullReportLabel(total)}
    </button>
  );
}

/**
 * Оболочка полосы и одна карточка — общие у ДВУХ полос: гостевой (превью с
 * сервера) и полосы вошедшего (полные тексты его тарифа, NatalFullStrip).
 * Разметка живёт здесь одна: это буквально «та же полоса в три столбца»,
 * и разъехаться двум её версиям нельзя.
 *
 * Этот файл НАМЕРЕННО лёгкий — ни строчки корпуса трактовок. Полные тексты
 * собирает ChartTabs.buildStripBlocks в ленивом куске, сюда они приезжают
 * готовыми абзацами.
 */
// Потолок ТЕКСТОВОЙ части карточки. Заголовок раздела, подзаголовок и нижняя
// строка-ссылка в него не входят и видны всегда.
//
// Откуда взялось. У владельца под ПРО тексты разошлись втрое: «Твои проявления»
// 894 знака, «Твои сферы жизни» 392, «Сценарии жизни» 273. Первая карточка
// вытягивала полосу за 349 px, две другие стояли с пустотой снизу, а на
// телефоне первая занимала страницу целиком и уносила следующий блок за кадр.
// Потолок делает высоту полосы свойством ВЁРСТКИ, а не длины текста.
//
// На узком экране потолок жёстче: там карточки идут в столбик, и каждая лишняя
// строка уводит следующий блок страницы из кадра.
// 25.09.2026: полоса ужата — шрифт 12px вместо 13px, отступы меньше,
// потолок ниже. Полный текст по-прежнему в разделе под полосой.
const BODY_CAP_MOBILE = 96;     // ~6 строк по 12px/leading-snug, карточка ≈ 165 px
const BODY_CAP_DESKTOP = 140;   // ~9 строк, карточка ≈ 210 px
const DESKTOP_MQ = '(min-width: 768px)';   // тот же порог, что у md: в Tailwind

/**
 * Полоса из трёх карточек одной высоты.
 *
 * Высоту текстовых блоков равняет JS, а не CSS, и это не прихоть. На десктопе
 * три карточки лежат в одном ряду грида и выравниваются сами, но НА ТЕЛЕФОНЕ
 * столбец — это три отдельных ряда по одной карточке, и растягивать там нечего:
 * CSS не умеет равнять высоту между рядами. Без общего замера у ЛАЙТА на 390 px
 * выходило 205/205/156.
 *
 * Высота общая и равна min(потолок, самый длинный текст в этой полосе) —
 * поэтому короткая гостевая полоса (36-72 px текста) не раздувается до
 * потолка пустотой, а длинная полоса вошедшего упирается в потолок и режется.
 *
 * Пишем в style и data-атрибут напрямую, без состояния: измерение идёт ПОСЛЕ
 * раскладки, а состояние вызвало бы вторую отрисовку ради того же результата.
 */
export function StripGrid({ children }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const apply = () => {
      const bodies = Array.from(root.querySelectorAll('[data-strip-body]'));
      if (!bodies.length) return;
      // Снимаем прошлую высоту, иначе замерим её же, а не натуральную.
      bodies.forEach((b) => { b.style.height = ''; });
      let cap = BODY_CAP_MOBILE;
      try {
        if (window.matchMedia(DESKTOP_MQ).matches) cap = BODY_CAP_DESKTOP;
      } catch (_e) { /* очень старый браузер — остаётся телефонный потолок */ }
      const natural = bodies.reduce((m, b) => Math.max(m, b.scrollHeight), 0);
      const h = Math.min(cap, natural);
      bodies.forEach((b) => {
        b.style.height = `${h}px`;
        // Затухание — только там, где текст РЕАЛЬНО не поместился: градиент
        // над пустотой читался бы как поломка, а не как «дальше есть ещё».
        b.dataset.clipped = b.scrollHeight - h > 4 ? 'true' : 'false';
      });
    };

    apply();
    // Шрифты доезжают после первой отрисовки и меняют перенос строк; ширина
    // колонки — на повороте телефона. Оба случая пересчитываем.
    const later = window.setTimeout(apply, 400);
    window.addEventListener('resize', apply);
    return () => {
      window.clearTimeout(later);
      window.removeEventListener('resize', apply);
    };
  });

  return (
    <div
      ref={rootRef}
      className="grid grid-cols-1 items-stretch gap-2 md:grid-cols-3"
      data-testid="strip-grid"
    >
      {children}
    </div>
  );
}

/**
 * note — приписка под текстом карточки. Сейчас это одна пометка: «возможен
 * соседний знак» у Луны, когда время рождения неизвестно. Стоит В КАРТОЧКЕ,
 * а не в разделе под ней: полоса — первое, что человек видит после расчёта,
 * и знак Луны он запоминает именно отсюда.
 */
export function StripCard({ blockKey, title, heading, paragraphs, onOpen, testId, note = null }) {
  return (
    <div
      className="flex h-full flex-col rounded-lg border border-accent-cta-100 bg-white px-2.5 py-2 shadow-sm"
      data-testid={testId || `natal-preview-${blockKey}`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wide text-accent-cta-800">
        {title}
      </div>
      <div className="mt-0.5 text-[12px] font-semibold leading-snug text-slate-800">
        {heading}
      </div>
      <div
        data-strip-body
        data-clipped="false"
        className="group relative mt-1 overflow-hidden"
        data-testid={`strip-body-${blockKey}`}
      >
        {(paragraphs || []).map((t, i) => (
          <p key={i} className={`text-[12px] leading-snug text-slate-600 ${i ? 'mt-1' : ''}`}>
            {t}
          </p>
        ))}
        {/* Затухание к фону карточки. В разметке лежит всегда, видно только
            когда полоса пометила блок обрезанным — так не нужно второй
            отрисовки ради одного градиента. */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b
                     from-transparent to-white opacity-0 transition-opacity
                     group-data-[clipped=true]:opacity-100"
          data-testid={`strip-fade-${blockKey}`}
        />
      </div>
      {note}

      {/* mt-auto прижимает строку к низу карточки: три карточки стоят одной
          высотой, и низ короткой должен читаться как край карточки, а не как
          забытая пустота. Действие у карточки по-прежнему ОДНО — эта строка.
          Второй кнопки и разворачивания на месте здесь нет намеренно: полный
          текст живёт в разделе под полосой, и двух дверей в него быть не должно. */}
      <button
        type="button"
        onClick={() => { if (typeof onOpen === 'function') onOpen(blockKey); }}
        className="mt-auto pt-1 text-left text-[11px] font-semibold text-sky-700 hover:text-sky-800"
        data-testid={`natal-preview-link-${blockKey}`}
      >
        {SECTION_CTA[blockKey] || 'Открыть раздел'} →
      </button>
    </div>
  );
}

export default function NatalPreviewBlocks({ chart, onOpen, onFullReport, onTotal }) {
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(false);

  const payload = useMemo(() => {
    if (!chart) return null;
    return {
      planets: chart.planets || [],
      houses: chart.houses || [],
      aspects: chart.aspects || [],
    };
  }, [chart]);

  useEffect(() => {
    if (!payload) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/natal/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        // Превью — украшение экрана, а не его условие: молчим и не ломаем
        // остальную страницу.
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [payload]);

  const blocks = (data && data.blocks) || [];
  const total = (data && data.total) || 0;
  const shown = blocks.length;
  // Флаг ставит сервер: он один знает знак Луны на 00:00 и 23:59 по месту
  // рождения (см. moon_changes_sign).
  const moonSignMayShift = !!(chart && chart.moon_sign_may_shift);

  // Раскрытие: до расчёта компонента нет вовсе, после — полоса вырастает
  // из нулевой высоты. Кадр задержки нужен, чтобы браузер успел взять
  // стартовое состояние и проиграть переход, а не прыгнуть сразу.
  useEffect(() => {
    if (!shown) {
      setVisible(false);
      return undefined;
    }
    // setTimeout, а не requestAnimationFrame: в фоновой вкладке rAF не
    // вызывается, и полоса осталась бы схлопнутой в нулевую высоту.
    const id = window.setTimeout(() => setVisible(true), 30);
    return () => window.clearTimeout(id);
  }, [shown]);

  // natal_preview — ОТДЕЛЬНОЕ событие на факт показа превью. natal_limit
  // сюда не переносится: он остаётся на открытии NatalLimitModal, иначе
  // сравнение «до/после» по цели 603392568 станет невозможным.
  useEffect(() => {
    if (!shown) return;
    try {
      if (sessionStorage.getItem(PREVIEW_SENT_KEY)) return;
      sessionStorage.setItem(PREVIEW_SENT_KEY, '1');
    } catch (e) {
      // приватный режим — считаем показ первым и отправляем один раз
      // за монтирование
    }
    ymGoal('natal_preview');
  }, [shown]);

  // Число блоков нужно ещё и мобильному дублю кнопки — он стоит в гриде
  // героя, под колесом, то есть вне этого компонента. Отдаём наверх ровно
  // то же total, что печатает кнопка здесь: два числа расходиться не должны.
  useEffect(() => {
    if (typeof onTotal !== 'function') return;
    onTotal(total > shown ? total : 0);
  }, [onTotal, total, shown]);

  if (!shown) return null;

  const open = (key) => {
    if (typeof onOpen === 'function') onOpen(key);
  };

  return (
    <>
    <div
      className={`overflow-hidden transition-all duration-500 ease-out ${
        visible ? 'mt-2 max-h-[1200px] opacity-100' : 'mt-0 max-h-0 opacity-0'
      }`}
      data-testid="natal-preview"
    >
      <StripGrid>
        {blocks.map((b) => (
          <StripCard
            key={b.key}
            blockKey={b.key}
            title={b.title}
            heading={b.heading}
            paragraphs={[b.text]}
            onOpen={open}
            /* Карточка «Твои проявления» — это планеты в знаках, и знак Луны
               человек читает именно здесь. Если время рождения неизвестно, а
               Луна в этот день меняла знак, сказать об этом надо тут, а не
               только в разделе, куда ещё надо нажать. */
            note={b.key === 'planets' && moonSignMayShift ? <MoonSignShiftNote /> : null}
          />
        ))}
      </StripGrid>

      {total > shown && (
        <div className="mt-2 flex justify-center">
          <FullReportButton
            total={total}
            onOpenBlocks={() => open(blocks[0].key)}
            onFullReport={onFullReport}
          />
        </div>
      )}

    </div>

    </>
  );
}
